<template>
  <component
    :is="expandable ? 'UiExpandablePanel' : 'div'"
    :expanded="expandable ? panelExpanded : null"
    @toggle="expandable ? toggleExpandablePanel() : null"
  >
    <template #title>
      <div class="onboarding-wizard-sidebar__section--title">
        <h3>{{ $t('Account Setup') }}</h3>
        <span>{{ displayedStepNumber }} / {{ onboardingWizardStepsTotal }}</span>
      </div>
    </template>
    <template v-if="!expandable">
      <div class="onboarding-wizard-sidebar__section--title">
        <h3>{{ $t('Account Setup') }}</h3>
        <span>{{ displayedStepNumber }} / {{ onboardingWizardStepsTotal }}</span>
      </div>
    </template>
    <nav
      :aria-label="$t('Account Setup')"
      class="onboarding-wizard-sidebar__nav"
    >
      <ul class="onboarding-wizard-sidebar__ul">
        <li>
          <span class="onboarding-wizard-sidebar__item completed">
            <span class="onboarding-wizard-sidebar__text">
              <Fa
                :icon="$options.faRocket"
                class="ui-icon--small"
              />
              {{ $t('Create account') }}
            </span>
            <span class="onboarding-wizard-sidebar__icon">
              <Fa
                :icon="$options.faCircleCheck"
                class="ui-icon--medium"
              />
            </span>
          </span>
        </li>
        <li
          v-for="step in getPrimaryStepsOnboardingWizard"
          :id="step.id"
          :key="step.id"
        >
          <span
            :data-test="`onboarding-wizard-sidebar-list-item-${step.id}`"
            class="onboarding-wizard-sidebar__item"
            :class="[
              {
                completed: step.is_complete,
                disabled: isStepDisabled(step),
                active: isStepActive(step.id),
              },
            ]"
          >
            <span class="onboarding-wizard-sidebar__text">
              <Fa
                :icon="steps[step.id].icon"
                class="ui-icon--small"
              />
              {{ steps[step.id].title }}
            </span>
            <transition name="sc-fade">
              <span
                v-if="step.is_complete"
                class="onboarding-wizard-sidebar__icon"
              >
                <Fa
                  :icon="$options.faCircleCheck"
                  class="ui-icon--medium"
                />
              </span>
            </transition>
          </span>
          <UiTooltip
            v-if="step.is_complete"
            :for="step.id"
            :text="$t('onboarding.tip_about_editing')"
          />
        </li>
      </ul>
    </nav>
  </component>
</template>

<script>
import { mapGetters } from 'vuex'
import Fa from 'vue-fa'
import {
  faRocket,
  faCircleCheck,
  faUser,
  faPhone,
  faShop,
  faStar,
  faAddressCard,
  faEuroSign,
  faTruck,
} from '@fortawesome/pro-solid-svg-icons'
import { UiExpandablePanel, UiTooltip } from '@sendcloud/ui-library'

export default {
  name: 'OWSidebarList',

  faRocket,
  faCircleCheck,
  faUser,
  faShop,
  faStar,
  faAddressCard,
  faEuroSign,
  faTruck,

  components: {
    Fa,
    UiExpandablePanel,
    UiTooltip,
  },

  props: {
    expandable: {
      type: Boolean,
      required: false,
    },
  },

  emits: ['close-mobile-navigation'],

  data() {
    return {
      panelExpanded: false,
    }
  },

  computed: {
    ...mapGetters([
      'getPrimaryStepsOnboardingWizard',
      'onboardingWizardStepsTotal',
      'onboardingWizardStepsCompleted',
    ]),

    displayedStepNumber() {
      if (!this.$route.path.includes('/onboarding/')) {
        return this.onboardingWizardStepsCompleted
      }

      const currentStepIndex = this.getPrimaryStepsOnboardingWizard.findIndex(({ id }) => id === this.stepId)
      // Add 2 to currentStepIndex because the steps array doesn't include the first step (is hardcoded)
      // and we need a 1-based index offset.
      return currentStepIndex + 2
    },

    stepId() {
      return this.$route.path.split('/onboarding/')[1] || []
    },

    steps() {
      return {
        'your-details': {
          title: this.$t('Your details'),
          icon: faUser,
        },
        'your-telephone': {
          title: this.$t('onboarding_wizard.your_telephone.heading'),
          icon: faPhone,
        },
        'integrations': {
          title: this.$t('Connect your store'),
          icon: faShop,
        },
        'your-address': {
          title: this.$t('Add your address'),
          icon: faAddressCard,
        },
        'payment-method': {
          title: this.$t('Payment details'),
          icon: faEuroSign,
        },
        'carriers': {
          title: this.$t('onboarding_wizard.carriers.page_title'),
          icon: faTruck,
        },
        'your-brand': {
          title: this.$t('Your brand'),
          icon: faStar,
        },
      }
    },

  },

  methods: {
    toggleSidebar() {
      this.$emit('close-mobile-navigation')
    },

    toggleExpandablePanel() {
      // referring to PointerEvent because UiExpandablePanel doesn't pass event payload
      event.stopPropagation()
      this.panelExpanded = !this.panelExpanded
    },

    isStepActive(stepId) {
      return this.stepId.includes(stepId)
    },

    isStepDisabled(step) {
      return !this.isStepActive(step.id) && !step.is_complete
    },
  },
}
</script>

<style lang="scss" scoped>
.onboarding-wizard-sidebar__section--title {
  padding: $sc-spacing-8 $sc-spacing-8 $sc-spacing-8 $sc-spacing-12;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  h3 {
    padding: 0;
    margin: 0;
    font-size: 1.125rem;
  }

  span {
    font-size: 1.125rem;
  }
}

.onboarding-wizard-sidebar__nav {
  padding: 0;
  margin: 0;
}

.onboarding-wizard-sidebar__ul {
  padding: 0;
  margin: 0;
  list-style: none;
  width: 100%;
}

.onboarding-wizard-sidebar__text {
  display: flex;
  align-items: center;
  transition: text-decoration 200ms;
  color: $sc-dark-blue;

  svg {
    margin-right: $sc-spacing-12;
  }
}

.onboarding-wizard-sidebar__item {
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: $sc-spacing-12 $sc-spacing-8 $sc-spacing-12 $sc-spacing-12;
  border-radius: $sc-border-radius;
  border: none;
  text-decoration: none;
  background-color: transparent;

  &.active {
    font-weight: 600;
    background-color: $sc-informative-10;
  }

  &:hover {
    background: color.mix($sc-informative, #fff, 5%);
  }

  &.completed,
  &.disabled {
    .onboarding-wizard-sidebar__text {
      color: $sc-dark-blue-75;
    }
  }

  &.disabled {
    .onboarding-wizard-sidebar__text {
      opacity: 0.75;
    }
  }

  &.completed {
    .onboarding-wizard-sidebar__text {
      text-decoration: line-through;
    }
  }

}

.onboarding-wizard-sidebar__icon {
  display: inline-flex;

  svg {
    color: $sc-positive;
  }
}
</style>

<style lang="scss">
// overwrite the styles coming from UiExpandablePanel
.onboarding-wizard-dropdown .ui-panel {
  border: none;
}

.onboarding-wizard-dropdown .ui-expandable-panel__toggle,
.onboarding-wizard-dropdown .ui-expandable-panel__body {
  padding: 0;
}

.onboarding-wizard-dropdown .ui-expandable-panel__toggle {
  font-weight: inherit;
}

.onboarding-wizard-dropdown .ui-expandable-panel__toggle:hover {
  text-decoration: none;
}
</style>
